import React, { useState, useRef, useMemo, useEffect } from 'react';
import ArrowDropDownIcon from '@mui/icons-material/ArrowDropDown';
import SearchIcon from '@mui/icons-material/Search';
import {
  Box,
  FormControl,
  InputAdornment,
  SxProps,
  Theme,
  ClickAwayListener,
  Tooltip,
} from '@mui/material';
import {
  SelectContainer,
  StyledTextField,
  StyledInputAdornment,
  SearchInput,
  DropdownContainer,
  OptionsContainer,
  EmptyStateContainer,
  EmptyStateText,
  OptionRow,
  ColumnCell,
  ArrowIconStyles,
} from './styles';
import EllipsisTooltipCell from '../EllipsisTooltipCell';
import useTanstackVirtualizer from '@hooks/useTanstackVirtualizer';

export interface TableSelectColumn<T = Record<string, unknown>> {
  key: keyof T;
  label: string;
  width?: string;
  flex?: number;
  render?: (value: T[keyof T], row: T) => React.ReactNode;
}

export interface TableSelectOption {
  [key: string]: unknown;
}

interface TableSelectFieldProps<T extends TableSelectOption> {
  label: string;
  options: T[];
  columns: TableSelectColumn<T>[];
  valueKey: keyof T;
  displayKey: keyof T;
  searchKeys?: (keyof T)[];
  selectedValue?: T[keyof T];
  onChange?: (value: T[keyof T], option: T) => void;
  onBlur?: () => void;
  disabled?: boolean;
  required?: boolean;
  error?: boolean;
  selectStyles?: SxProps<Theme>;
  rowStyles?: SxProps<Theme>;
  placeholder?: string;
  searchPlaceholder?: string;
  isOptionDisabled?: (option: T) => boolean;
  renderValue?: (option: T) => string;
  renderValueAdornment?: (option: T) => React.ReactNode;
  dropdownWidth?: string | number;
  centerDropdown?: boolean;
  dropdownAlign?: 'left' | 'right';
}

const TableSelectField = <T extends TableSelectOption>({
  label,
  options,
  columns,
  valueKey,
  displayKey,
  searchKeys,
  selectedValue,
  onChange = () => {},
  onBlur = () => {},
  disabled = false,
  required = false,
  error = false,
  selectStyles = {},
  rowStyles = {},
  placeholder,
  searchPlaceholder = 'Search...',
  isOptionDisabled,
  renderValue,
  renderValueAdornment,
  dropdownWidth = '450px',
  centerDropdown = false,
  dropdownAlign = 'left',
}: TableSelectFieldProps<T>) => {
  const [showMenu, setShowMenu] = useState(false);
  const [searchValue, setSearchValue] = useState('');
  const [isSelectedValueEllipsized, setIsSelectedValueEllipsized] = useState(false);
  const [dropdownLeft, setDropdownLeft] = useState<number | null>(null);
  const selectRef = useRef<HTMLDivElement>(null);
  const dropdownRef = useRef<HTMLDivElement>(null);
  const searchInputRef = useRef<HTMLInputElement>(null);
  const selectedInputRef = useRef<HTMLInputElement>(null);
  const optionsContainerRef = useRef<HTMLDivElement>(null);

  const keysToSearch = searchKeys || [displayKey];

  const selectedOption = useMemo(
    () => options.find((opt) => opt[valueKey] === selectedValue),
    [options, valueKey, selectedValue],
  );

  const displayValue = useMemo(() => {
    if (!selectedOption) return '';
    if (renderValue) return renderValue(selectedOption);
    return String(selectedOption[displayKey] || '');
  }, [selectedOption, renderValue, displayKey]);

  const filteredOptions = useMemo(() => {
    if (!searchValue) return options;
    const lowerSearch = searchValue.toLowerCase();
    return options.filter((option) =>
      keysToSearch.some((key) =>
        String(option[key] || '')
          .toLowerCase()
          .includes(lowerSearch),
      ),
    );
  }, [options, searchValue, keysToSearch]);

  const { rowVirtualizer } = useTanstackVirtualizer({
    rows: filteredOptions,
    parentRef: optionsContainerRef,
    hasNextPage: false,
    isFetchingNextPage: false,
    fetchNextPage: () => {},
  });

  const updateDropdownPosition = () => {
    const fieldEl = selectRef.current;
    const dropdownEl = dropdownRef.current;
    if (!fieldEl || !dropdownEl) return;

    if (!centerDropdown) {
      setDropdownLeft(0);
      return;
    }

    const fieldRect = fieldEl.getBoundingClientRect();
    const dropdownWidth = dropdownEl.offsetWidth;
    const viewportWidth = window.innerWidth;
    const padding = 8;

    // Try to center the dropdown relative to the field
    let left = (fieldRect.width - dropdownWidth) / 2;

    // Convert to absolute viewport position
    const absLeft = fieldRect.left + left;
    const absRight = absLeft + dropdownWidth;

    // Clamp to viewport bounds
    if (absRight > viewportWidth - padding) {
      left -= absRight - (viewportWidth - padding);
    }
    if (absLeft < padding) {
      left += padding - absLeft;
    }

    setDropdownLeft(left);
  };

  useEffect(() => {
    if (!showMenu) return;
    // Measure after dropdown renders
    requestAnimationFrame(updateDropdownPosition);
    window.addEventListener('resize', updateDropdownPosition);
    return () => window.removeEventListener('resize', updateDropdownPosition);
  }, [showMenu]);

  useEffect(() => {
    const element = selectedInputRef.current;
    if (!element) return;

    const checkEllipsis = () => {
      setIsSelectedValueEllipsized(element.scrollWidth > element.clientWidth);
    };

    checkEllipsis();

    window.addEventListener('resize', checkEllipsis);
    return () => window.removeEventListener('resize', checkEllipsis);
  }, [displayValue]);

  const handleSelectClose = () => {
    setShowMenu(false);
    setSearchValue('');
    setDropdownLeft(null);
    onBlur();
  };

  const handleOptionSelect = (option: T) => {
    onChange(option[valueKey], option);
    setShowMenu(false);
    setSearchValue('');
  };

  const handleFieldClick = () => {
    if (!disabled) {
      setShowMenu((prev) => !prev);
    }
  };

  return (
    <ClickAwayListener
      onClickAway={() => {
        if (showMenu) handleSelectClose();
      }}
    >
      <FormControl fullWidth>
        <SelectContainer ref={selectRef} sx={selectStyles}>
          <Tooltip title={isSelectedValueEllipsized ? displayValue : ''} arrow placement="top">
            <StyledTextField
              disabled={disabled}
              label={label}
              required={required}
              placeholder={placeholder}
              onClick={handleFieldClick}
              value={displayValue}
              sx={selectStyles}
              inputRef={selectedInputRef}
              InputProps={{
                readOnly: true,
                startAdornment:
                  renderValueAdornment && selectedOption
                    ? renderValueAdornment(selectedOption)
                    : undefined,
                endAdornment: (
                  <StyledInputAdornment position="end">
                    <ArrowDropDownIcon sx={ArrowIconStyles(showMenu)} />
                  </StyledInputAdornment>
                ),
              }}
              error={error}
            />
          </Tooltip>

          {showMenu && (
            <DropdownContainer
              ref={dropdownRef}
              sx={{
                ...(dropdownAlign === 'right'
                  ? { right: 0, left: 'auto' }
                  : { left: dropdownLeft }),
                visibility:
                  dropdownAlign === 'right' || dropdownLeft !== null ? 'visible' : 'hidden',
                width: dropdownWidth,
              }}
            >
              <SearchInput
                inputRef={searchInputRef}
                size="small"
                placeholder={searchPlaceholder}
                value={searchValue}
                onChange={(e: React.ChangeEvent<HTMLInputElement>) =>
                  setSearchValue(e.target.value)
                }
                onKeyDown={(e: React.KeyboardEvent) => e.stopPropagation()}
                InputProps={{
                  startAdornment: (
                    <InputAdornment position="start">
                      <SearchIcon sx={{ fontSize: 18, color: '#9E9E9E' }} />
                    </InputAdornment>
                  ),
                }}
              />
              <OptionsContainer ref={optionsContainerRef}>
                {filteredOptions.length === 0 ? (
                  <EmptyStateContainer>
                    <EmptyStateText>No options found</EmptyStateText>
                  </EmptyStateContainer>
                ) : (
                  <Box sx={{ height: rowVirtualizer.getTotalSize(), position: 'relative' }}>
                    {rowVirtualizer.getVirtualItems().map((virtualRow) => {
                      const option = filteredOptions[virtualRow.index];
                      const isSelected = option[valueKey] === selectedValue;
                      const isDisabled = isOptionDisabled ? isOptionDisabled(option) : false;

                      return (
                        <OptionRow
                          key={String(option[valueKey]) || virtualRow.index}
                          data-index={virtualRow.index}
                          ref={rowVirtualizer.measureElement}
                          onClick={() => !isDisabled && handleOptionSelect(option)}
                          isSelected={isSelected}
                          className={isDisabled ? 'disabled' : ''}
                          sx={{
                            position: 'absolute',
                            top: 0,
                            left: 0,
                            right: 0,
                            height: 'fit-content',
                            transform: `translateY(${virtualRow.start}px)`,
                            borderBottom:
                              virtualRow.index < filteredOptions.length - 1
                                ? '1px solid #F0F0F0'
                                : 'none',
                            ...(rowStyles as object),
                          }}
                        >
                          {columns.map((column) => {
                            const cellValue = option[column.key];
                            const displayText = String(cellValue ?? '-');

                            return (
                              <ColumnCell
                                key={String(column.key)}
                                columnWidth={column.width}
                                columnFlex={column.flex}
                              >
                                <EllipsisTooltipCell title={displayText} isDisabled={isDisabled}>
                                  {column.render ? column.render(cellValue, option) : displayText}
                                </EllipsisTooltipCell>
                              </ColumnCell>
                            );
                          })}
                        </OptionRow>
                      );
                    })}
                  </Box>
                )}
              </OptionsContainer>
            </DropdownContainer>
          )}
        </SelectContainer>
      </FormControl>
    </ClickAwayListener>
  );
};

export default TableSelectField;
